import React, { useEffect, useRef, useState } from 'react';
import {
  Table, Thead, Tbody, Tr, Th, Td, Button, Modal, ModalOverlay, ModalContent,
  ModalHeader, ModalBody, ModalFooter, useDisclosure, Badge, Flex, Text, Box,
  HStack, Input, useToast, FormControl, FormLabel, SimpleGrid, Textarea, Switch,
  AlertDialog, AlertDialogBody, AlertDialogFooter, AlertDialogHeader,
  AlertDialogContent, AlertDialogOverlay,
} from '@chakra-ui/react';
import { AddIcon } from '@chakra-ui/icons';
import deportesApi from '../../../api/deportesApi';
import { Paginacion, ROJO, ROJO_HOVER } from '../_shared';

export interface CampoCatalogo {
  key: string;
  label: string;
  tipo?: 'text' | 'number' | 'textarea';
  requerido?: boolean;
}

interface Props {
  recurso: string;          // disciplinas | categorias | ramas | categoria-inventario
  titulo: string;
  campos: CampoCatalogo[];
}

const TablaCatalogo: React.FC<Props> = ({ recurso, titulo, campos }) => {
  const toast = useToast();
  const cancelRef = useRef<any>(null);
  const { isOpen: isForm, onOpen: onForm, onClose: onCloseForm } = useDisclosure();
  const { isOpen: isEliminar, onOpen: onEliminar, onClose: onCloseEliminar } = useDisclosure();

  const [data, setData] = useState<any[]>([]);
  const [cargando, setCargando] = useState(false);
  const [procesando, setProcesando] = useState(false);
  const [busqueda, setBusqueda] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const [modo, setModo] = useState<'new' | 'edit'>('new');
  const [sel, setSel] = useState<any>(null);
  const [form, setForm] = useState<any>({});

  const fetchData = async () => {
    setCargando(true);
    try { setData(await deportesApi.listarCatalogo(recurso) || []); }
    catch { setData([]); } finally { setCargando(false); }
  };
  useEffect(() => { fetchData(); /* eslint-disable-next-line */ }, [recurso]);

  const datosFiltrados = data.filter((d) =>
    busqueda
      ? (d.nombre || '').toLowerCase().includes(busqueda.toLowerCase()) ||
        (d.codigo || '').toLowerCase().includes(busqueda.toLowerCase())
      : true
  );
  const totalPages = Math.ceil(datosFiltrados.length / itemsPerPage);
  const indexUltimo = currentPage * itemsPerPage;
  const indexPrimero = indexUltimo - itemsPerPage;
  const itemsPagina = datosFiltrados.slice(indexPrimero, indexUltimo);

  const abrirNuevo = () => {
    setModo('new');
    const init: any = { activo: true };
    campos.forEach((c) => (init[c.key] = ''));
    setForm(init);
    onForm();
  };
  const abrirEditar = (item: any) => {
    setModo('edit');
    setSel(item);
    const init: any = { activo: item.activo };
    campos.forEach((c) => (init[c.key] = item[c.key] ?? ''));
    setForm(init);
    onForm();
  };

  const guardar = async () => {
    const requeridos = campos.filter((c) => c.requerido);
    for (const c of requeridos) {
      if (!form[c.key]) { toast({ title: `${c.label} es obligatorio`, status: 'warning', duration: 2500 }); return; }
    }
    setProcesando(true);
    try {
      const payload: any = { ...form };
      campos.forEach((c) => {
        if (c.tipo === 'number') payload[c.key] = form[c.key] === '' ? null : Number(form[c.key]);
      });
      if (modo === 'edit') await deportesApi.actualizarCatalogo(recurso, sel.id, payload);
      else await deportesApi.crearCatalogo(recurso, payload);
      toast({ title: modo === 'edit' ? 'Registro actualizado' : 'Registro creado', status: 'success', duration: 2500 });
      onCloseForm(); fetchData();
    } catch (e: any) {
      toast({ title: 'Error', description: e?.response?.data?.message || 'No se pudo guardar.', status: 'error', duration: 4000 });
    } finally { setProcesando(false); }
  };

  const eliminar = async () => {
    if (!sel) return;
    setProcesando(true);
    try {
      await deportesApi.eliminarCatalogo(recurso, sel.id);
      toast({ title: 'Registro eliminado', status: 'success', duration: 2500 });
      onCloseEliminar(); fetchData();
    } catch (e: any) { toast({ title: 'Error', description: e?.response?.data?.message, status: 'error', duration: 4000 }); }
    finally { setProcesando(false); }
  };

  return (
    <Box>
      <Flex mb={3} justify="space-between" align="center" flexWrap="wrap" gap={2}>
        <Input placeholder={`Buscar en ${titulo.toLowerCase()}...`} value={busqueda} onChange={(e) => { setBusqueda(e.target.value); setCurrentPage(1); }} size="sm" w={{ base: 'full', md: '260px' }} />
        <Button size="sm" leftIcon={<AddIcon />} bg={ROJO} color="white" _hover={{ bg: ROJO_HOVER }} onClick={abrirNuevo}>Nuevo</Button>
      </Flex>

      <Box overflowX="auto" borderRadius="md" boxShadow="md" bg="white">
        <Table variant="simple" size="sm">
          <Thead bg={ROJO}>
            <Tr>
              <Th color="white" fontSize="xs">Código</Th>
              {campos.filter((c) => c.key !== 'codigo').map((c) => <Th key={c.key} color="white" fontSize="xs">{c.label}</Th>)}
              <Th color="white" fontSize="xs">Activo</Th>
              <Th color="white" fontSize="xs">Acciones</Th>
            </Tr>
          </Thead>
          <Tbody>
            {cargando ? (
              <Tr><Td colSpan={campos.length + 2} textAlign="center" py={8}><Text color="gray.500">Cargando...</Text></Td></Tr>
            ) : itemsPagina.length === 0 ? (
              <Tr><Td colSpan={campos.length + 2} textAlign="center" py={8}><Text color="gray.400">Sin registros</Text></Td></Tr>
            ) : itemsPagina.map((item, idx) => (
              <Tr key={item.id} bg={idx % 2 === 0 ? 'white' : 'gray.50'} _hover={{ bg: 'red.50' }}>
                <Td fontWeight="medium">{item.codigo}</Td>
                {campos.filter((c) => c.key !== 'codigo').map((c) => (
                  <Td key={c.key} fontSize="sm" maxW="240px"><Text noOfLines={2}>{item[c.key] ?? '—'}</Text></Td>
                ))}
                <Td><Badge colorScheme={item.activo ? 'green' : 'gray'}>{item.activo ? 'Sí' : 'No'}</Badge></Td>
                <Td>
                  <HStack spacing={1}>
                    <Button size="xs" colorScheme="orange" variant="outline" onClick={() => abrirEditar(item)}>Editar</Button>
                    <Button size="xs" colorScheme="red" variant="outline" onClick={() => { setSel(item); onEliminar(); }}>Eliminar</Button>
                  </HStack>
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </Box>

      <Paginacion currentPage={currentPage} totalPages={totalPages} total={datosFiltrados.length} indexPrimero={indexPrimero} indexUltimo={indexUltimo} onChange={setCurrentPage} />

      <Modal isOpen={isForm} onClose={onCloseForm} size="lg" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader borderBottom="1px solid" borderColor="gray.200" color={ROJO}>
            {modo === 'edit' ? 'Editar' : 'Nuevo'} — {titulo}
          </ModalHeader>
          <ModalBody py={5}>
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
              {campos.map((c) => (
                <FormControl key={c.key} isRequired={c.requerido} gridColumn={c.tipo === 'textarea' ? { md: 'span 2' } : undefined}>
                  <FormLabel fontSize="sm">{c.label}</FormLabel>
                  {c.tipo === 'textarea' ? (
                    <Textarea rows={2} value={form[c.key] ?? ''} onChange={(e) => setForm({ ...form, [c.key]: e.target.value })} />
                  ) : (
                    <Input type={c.tipo === 'number' ? 'number' : 'text'} value={form[c.key] ?? ''} onChange={(e) => setForm({ ...form, [c.key]: e.target.value })} />
                  )}
                </FormControl>
              ))}
              <FormControl display="flex" alignItems="center" gap={2}>
                <Switch colorScheme="red" isChecked={!!form.activo} onChange={(e) => setForm({ ...form, activo: e.target.checked })} />
                <FormLabel fontSize="sm" mb={0}>Activo</FormLabel>
              </FormControl>
            </SimpleGrid>
          </ModalBody>
          <ModalFooter gap={2}>
            <Button variant="outline" onClick={onCloseForm} isDisabled={procesando}>Cancelar</Button>
            <Button bg={ROJO} color="white" _hover={{ bg: ROJO_HOVER }} onClick={guardar} isLoading={procesando} loadingText="Guardando...">Guardar</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <AlertDialog isOpen={isEliminar} leastDestructiveRef={cancelRef} onClose={onCloseEliminar}>
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold" color={ROJO}>Eliminar registro</AlertDialogHeader>
            <AlertDialogBody>¿Eliminar <strong>{sel?.nombre}</strong>?</AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onCloseEliminar} isDisabled={procesando}>Cancelar</Button>
              <Button colorScheme="red" onClick={eliminar} ml={3} isLoading={procesando} loadingText="Eliminando...">Eliminar</Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
};

export default TablaCatalogo;
